import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { BoxTopperSettings, ColorMode, SealRow, SealTemplate } from "@/utils/boxTopper";

const SETTINGS_KEY = ["box-topper-settings"];
const SEALS_KEY = ["box-topper-seals"];
export const RECENT_SEAL_LIMIT = 100;

/** The browser may write only these columns (the migration's column grants);
 *  relay_profile and enabled_at are server-owned. */
export type BoxTopperSettingsUpdate = Partial<
  Pick<BoxTopperSettings, "enabled" | "mode" | "color_mode" | "template" | "offer_text" | "offer_terms" | "validity_days" | "item_rule">
>;

/** The signed-in store's Box Topper row (RLS-scoped, one per store). Not in
 *  the generated Database types yet, so it goes through an untyped client. */
export const useBoxTopperSettings = () => {
  const getSupabaseClient = useSupabaseClient();
  return useQuery<BoxTopperSettings | null>({
    queryKey: SETTINGS_KEY,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;
      const { data, error } = await sb.from("box_topper_settings").select("*").maybeSingle();
      if (error) throw error;
      return (data as BoxTopperSettings | null) ?? null;
    },
    // Carries the on/off switch: never trust a minutes-old copy.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

export const useUpdateBoxTopperSettings = () => {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ storeId, updates }: { storeId: string; updates: BoxTopperSettingsUpdate }) => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;
      const { data, error } = await sb
        .from("box_topper_settings")
        .update(updates)
        .eq("store_id", storeId)
        .select()
        .single();
      if (error) throw error;
      return data as BoxTopperSettings;
    },
    onSuccess: (row) => queryClient.setQueryData(SETTINGS_KEY, row),
  });
};

/** The newest seals, polled so a failure shows up without a reload. */
export const useBoxTopperSeals = () => {
  const getSupabaseClient = useSupabaseClient();
  return useQuery<SealRow[]>({
    queryKey: SEALS_KEY,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;
      const { data, error } = await sb
        .from("box_topper_seals")
        .select("*")
        .order("created_at", { ascending: false })
        .order("seal_index", { ascending: true })
        .limit(RECENT_SEAL_LIMIT);
      if (error) throw error;
      return (data as SealRow[] | null) ?? [];
    },
    staleTime: 0,
    refetchInterval: 15_000,
  });
};

type ActionBody = { action: "retry"; seal_id: string } | { action: "reprint"; order_id: number };

async function invokeBoxTopper<T>(getSupabaseClient: ReturnType<typeof useSupabaseClient>, body: unknown): Promise<T> {
  const supabase = await getSupabaseClient();
  if (!supabase) throw new Error("Not authenticated");
  const { data, error } = await supabase.functions.invoke("box-topper-print", { body });
  if (error) {
    // supabase-js hides a non-2xx body behind error.context; surface our message.
    const context = (error as { context?: Response }).context;
    const detail = context && typeof context.json === "function" ? await context.json().catch(() => null) : null;
    throw new Error(detail?.error ?? error.message ?? "The printer did not answer");
  }
  return data as T;
}

/** Retry one failed seal, or reprint every seal of an order. */
export const useBoxTopperAction = () => {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ActionBody) =>
      invokeBoxTopper<{ ok: boolean; seals: { status: string; error?: string }[] }>(getSupabaseClient, body),
    onSettled: () => queryClient.invalidateQueries({ queryKey: SEALS_KEY }),
  });
};

export interface SealPreviewInput {
  template: SealTemplate;
  color_mode: ColorMode;
  offer_text: string;
  offer_terms: string;
}

/** A relay dry_run render of the (unsaved) template: prints nothing. */
export const useSealPreview = () => {
  const getSupabaseClient = useSupabaseClient();
  return useMutation({
    mutationFn: (input: SealPreviewInput) =>
      invokeBoxTopper<{ ok: true; png_base64: string; warnings: string[] }>(getSupabaseClient, { action: "preview", ...input }),
  });
};
